import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [bugCount, setBugCount] = useState(0);
    const [loading, setLoading] = useState(true); // Add loading state

    const validateAndRefreshUserData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            const storedUserData = localStorage.getItem('user');
            if (!storedUserData) {
                localStorage.removeItem('token');
                setLoading(false);
                return;
            }

            try {
                const storedUser = JSON.parse(storedUserData);
                if (!storedUser || !storedUser.user) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    setLoading(false);
                    return;
                }

                // Validate token with server and get fresh user data
                const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.cuecumber.com.au/webmanage';
                const response = await fetch(`${BASE_URL}/api/validate-token`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Token validation failed');
                }

                const validatedData = await response.json();
                
                // Update user data with server response
                const userWithRole = {
                    token,
                    user: {
                        ...validatedData.user,
                        role: validatedData.role || storedUser.role || storedUser.user.role || 'user'
                    }
                };

                setUser(userWithRole);
                // Update localStorage with fresh data
                localStorage.setItem('user', JSON.stringify({
                    role: userWithRole.user.role,
                    user: userWithRole.user
                }));
            } catch (error) {
                console.error('Error validating user:', error);
                // Clear invalid state
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setUser(null);
            }
        } catch (error) {
            console.error('Error initializing auth state:', error);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    // Run validation on mount and set up periodic revalidation
    useEffect(() => {
        validateAndRefreshUserData();
        
        // Revalidate every 5 minutes
        const intervalId = setInterval(validateAndRefreshUserData, 5 * 60 * 1000);
        
        return () => clearInterval(intervalId);
    }, []);

    const login = (userData) => {
        if (!userData.token || !userData.user) {
            console.error('Invalid user data for login:', userData);
            return;
        }

        // Ensure user has a role
        const userWithRole = {
            ...userData,
            user: {
                ...userData.user,
                role: userData.role || userData.user.role || 'user'
            }
        };

        setUser(userWithRole);
        localStorage.setItem('token', userWithRole.token);
        localStorage.setItem('user', JSON.stringify({
            role: userWithRole.user.role,
            user: userWithRole.user
        }));
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    };

    const authContextValue = {
        user,
        loading,
        login,
        logout,
        bugCount,
        setBugCount,
        refreshUserData: validateAndRefreshUserData,
    };

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};